/**
 * Wishlist Functionality for Shopify Dawn Theme
 * – localStorage-based, no backend required
 * – Event delegation handles AJAX-filtered collection pages
 * – MutationObserver (on <main>) re-syncs heart states after filter/sort swaps
 * – Wishlist page renders cards using Dawn's EXACT card-product HTML structure
 */

if (!window.WishlistInitialized) {
window.WishlistInitialized = true;

localStorage.removeItem('shopify_dawn_wishlist');
const WISHLIST_KEY = 'shopify_dawn_wishlist_v2';

/* ─────────────────────────────────────────────
   Data Layer
───────────────────────────────────────────── */
const Wishlist = {
  getItems() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch { return []; }
  },

  saveItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  },

  has(productId) {
    return this.getItems().some((i) => String(i.id) === String(productId));
  },

  add(product) {
    const items = this.getItems();
    if (!this.has(product.id)) { items.push(product); this.saveItems(items); }
  },

  remove(productId) {
    this.saveItems(this.getItems().filter((i) => String(i.id) !== String(productId)));
  },

  toggle(product) {
    if (this.has(product.id)) { this.remove(product.id); return false; }
    this.add(product); return true;
  },

  count() { return this.getItems().length; },
  clear() { localStorage.removeItem(WISHLIST_KEY); },
};

/* ─────────────────────────────────────────────
   UI Helpers
───────────────────────────────────────────── */
function setButtonState(btn, isActive) {
  btn.classList.toggle('wishlist-btn--active', isActive);
  btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
  btn.setAttribute('aria-pressed', String(isActive));
  const icon = btn.querySelector('.wishlist-icon');
  if (icon) icon.setAttribute('fill', isActive ? 'currentColor' : 'none');
}

function updateWishlistCounter() {
  const count = Wishlist.count();
  document.querySelectorAll('.wishlist-count-bubble').forEach((el) => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function syncAllButtonStates() {
  // Read localStorage once and build a Set for O(1) lookups
  const ids = new Set(Wishlist.getItems().map((i) => String(i.id)));
  document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
    const id = btn.dataset.productId;
    if (id) setButtonState(btn, ids.has(String(id)));
  });
}

/* ─────────────────────────────────────────────
   Event Delegation
   One listener on document catches all wishlist
   buttons — including AJAX-injected ones.
───────────────────────────────────────────── */
function initWishlistDelegation() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    let swatches = btn.dataset.productSwatches || '';
    if (!swatches || swatches.trim() === '') {
      const card = btn.closest('.product-card-wrapper');
      if (card) {
        const swatchWrapper = card.querySelector('.filter-swatchs-wrapper');
        if (swatchWrapper) {
          swatches = swatchWrapper.innerHTML;
        }
      }
    }

    const product = {
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
      available: btn.dataset.productAvailable !== 'false',
      onSale: btn.dataset.productOnSale === 'true',
      compareAtPrice: btn.dataset.productCompareAtPrice || '',
      swatches: swatches,
    };

    if (!product.id) return;

    const added = Wishlist.toggle(product);
    setButtonState(btn, added);
    updateWishlistCounter();
    showToast(
      added
        ? `"${product.title}" added to wishlist`
        : `"${product.title}" removed from wishlist`
    );
  });
}

/* ─────────────────────────────────────────────
   MutationObserver
   Watches <main> (never replaced by Dawn AJAX)
   and re-syncs heart states after the product
   grid is swapped by filters / sorting.
───────────────────────────────────────────── */
function observeProductGrid() {
  const target = document.querySelector('main') || document.body;
  let debounce = null;

  new MutationObserver((mutations) => {
    if (!mutations.some((m) => m.addedNodes.length > 0)) return;
    clearTimeout(debounce);
    debounce = setTimeout(syncAllButtonStates, 200);
  }).observe(target, { childList: true, subtree: true });
}

/* ─────────────────────────────────────────────
   Toast
───────────────────────────────────────────── */
let toastTimer = null;

function showToast(message) {
  let toast = document.getElementById('wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('wishlist-toast--visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('wishlist-toast--visible'), 3000);
}

/* ─────────────────────────────────────────────
   Dawn Card HTML Builder
   Replicates card-product.liquid output exactly
   so wishlist cards are visually identical to
   every other product card in the theme.
───────────────────────────────────────────── */
function buildDawnCardHTML(item) {
  const isAvailable = item.available;
  const isOnSale = item.onSale;

  let badgeHTML = '';
  if (!isAvailable) {
    badgeHTML = `<div class="product-badge"><span class="badge badge-sold-out">Sold out</span></div>`;
  } else if (isOnSale) {
    badgeHTML = `<div class="product-badge"><span class="badge badge-sale">Sale</span></div>`;
  }

  const imageHTML = item.image
    ? `<img
        src="${escapeAttr(item.image)}"
        alt="${escapeAttr(item.title)}"
        class="motion-reduce"
        loading="lazy"
        width="465"
        height="600"
      >`
    : `<div class="media media--placeholder">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
          <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c-.1-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/>
        </svg>
      </div>`;

  const priceHTML = isOnSale
    ? `<price-main class="price-main">
        <price-sale class="price-sale">
          <span class="product-actual-price varies-price">${item.price}</span>
          <span class="discount-price">${item.compareAtPrice}</span>
        </price-sale>
      </price-main>`
    : `<price-main class="price-main">
        <price-sale class="price-sale">
          <span class="product-actual-price">${item.price}</span>
        </price-sale>
      </price-main>`;

  const heartBtnHTML = `
    <button
      type="button"
      class="wishlist-btn wishlist-btn--active"
      data-wishlist-btn
      data-product-id="${item.id}"
      data-product-title="${escapeAttr(item.title)}"
      data-product-url="${escapeAttr(item.url)}"
      data-product-image="${escapeAttr(item.image)}"
      data-product-price="${escapeAttr(item.price)}"
      data-product-available="${item.available}"
      data-product-on-sale="${item.onSale}"
      data-product-compare-at-price="${escapeAttr(item.compareAtPrice)}"
      aria-label="Remove from wishlist"
      aria-pressed="true"
    >
      <svg class="wishlist-icon" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="currentColor" stroke="currentColor"
        stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true" focusable="false">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>`;

  const hasSwatches = item.swatches && item.swatches.trim() !== '';
  const hoverBarClasses = hasSwatches ? "product-card-hover-bar tlpc-card-hover-bar" : "product-card-hover-bar tlpc-card-hover-bar no-swatches";

  const addBtnHTML = `
    <div class="${hoverBarClasses}">
      <div class="tlpc-card-swatches">
        ${hasSwatches ? `<div class="filter-swatchs-wrapper">${item.swatches}</div>` : ''}
      </div>
      <div class="tlpc-card-atc">
        <quick-view
          id="wishlist-qv-${item.id}"
          class="product-add-to-cart tlpc-add-to-cart"
          data-product-url="${escapeAttr(item.url)}"
          tabindex="0"
        >
          <a
            href="${escapeAttr(item.url)}"
            class="add-to-cart-icon tlpc-add-btn"
            data-quickview
            aria-label="Quick view"
            tabindex="-1"
          >
            <span class="quick-view-text">ADD</span>
            <span class="svg-icon hidden">           
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.6673 6.66679C10.6673 7.37404 10.3864 8.05231 9.88627 8.55241C9.38617 9.05251 8.70789 9.33346 8.00065 9.33346C7.29341 9.33346 6.61513 9.05251 6.11503 8.55241C5.61494 8.05231 5.33398 7.37404 5.33398 6.66679" stroke="white" stroke-width="0.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2.07031 4.02271H13.933" stroke="white" stroke-width="0.6" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2.26667 3.64454C2.09357 3.87533 2 4.15605 2 4.44454V13.3332C2 13.6868 2.14048 14.026 2.39052 14.276C2.64057 14.5261 2.97971 14.6665 3.33333 14.6665H12.6667C13.0203 14.6665 13.3594 14.5261 13.6095 14.276C13.8595 14.026 14 13.6868 14 13.3332V4.44454C14 4.15605 13.9064 3.87533 13.7333 3.64454L12.4 1.86654C12.2758 1.70094 12.1148 1.56654 11.9296 1.47397C11.7445 1.3814 11.5403 1.33321 11.3333 1.33321H4.66667C4.45967 1.33321 4.25552 1.3814 4.07038 1.47397C3.88524 1.56654 3.7242 1.70094 3.6 1.86654L2.26667 3.64454Z" stroke="white" stroke-width="0.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="spinner-loading-btn btn-loader hidden" data-spin-loader>
              <svg class="btn-loader-icon" viewBox="0 0 24 24">
                <circle cx="4" cy="12" r="3" fill="currentColor">
                  <animate id="svg3DotsScale0" attributeName="r" begin="0;svg3DotsScale1.end-0.375s" dur="1.125s" values="3;.2;3"></animate>
                </circle>
                <circle cx="12" cy="12" r="3" fill="currentColor">
                  <animate attributeName="r" begin="svg3DotsScale0.end-0.9s" dur="1.125s" values="3;.2;3"></animate>
                </circle>
                <circle cx="20" cy="12" r="3" fill="currentColor">
                  <animate id="svg3DotsScale1" attributeName="r" begin="svg3DotsScale0.end-0.675s" dur="1.125s" values="3;.2;3"></animate>
                </circle>
              </svg>
            </span>
          </a>
        </quick-view>
      </div>
    </div>`;

  return `
    <product-card-item class="product-card-item product-grid-style-card">
      <div class="product-card-wrapper">
        <div class="product-card-image">
          ${badgeHTML}
          <div class="media-wrapper" style="--media-ratio:100%">
            <a href="${escapeAttr(item.url)}" aria-label="${escapeAttr(item.title)}" tabindex="-1" data-main-image>
              ${imageHTML}
            </a>
            ${heartBtnHTML}
          </div>
          ${addBtnHTML}
        </div>
        <div class="product-card-content">
          <a href="${escapeAttr(item.url)}" class="collection-title">
            ${escapeHtml(item.title)}
          </a>
          ${priceHTML}
          <div class="tlpc-mobile-swatches-container"></div>
        </div>
      </div>
    </product-card-item>`;
}

/* ─────────────────────────────────────────────
   Wishlist Page Renderer
───────────────────────────────────────────── */
function autoInjectWishlistContainer() {
  if (document.getElementById('wishlist-page-container')) return;
  if (!window.location.pathname.includes('/pages/wishlist')) return;

  const rte = document.querySelector(
    'main .rte, main [class*="page-width"] .rte, main .page-width'
  );
  if (!rte) return;

  rte.innerHTML = `
    <div id="wishlist-page-container" style="padding:4rem 0;">
      <div class="wishlist-page__header" style="margin-bottom:3.2rem;">
        <h1 style="font-size:clamp(2.4rem,4vw,3.6rem);font-family:var(--font-heading-family);margin:0 0 .8rem;">My Wishlist</h1>
        <p id="wishlist-count-text" style="font-size:1.4rem;color:rgba(var(--color-foreground),.6);margin:0;"></p>
      </div>
      <div class="wishlist-empty" id="wishlist-empty-state"
        style="display:flex;flex-direction:column;align-items:center;gap:2rem;padding:6rem 2rem;text-align:center;">
        <svg style="width:5.6rem;height:5.6rem;color:rgba(var(--color-foreground),.2)"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <h2 style="font-size:2rem;font-family:var(--font-heading-family);margin:0;">Your wishlist is empty</h2>
        <p style="font-size:1.4rem;color:rgba(var(--color-foreground),.55);margin:0;max-width:36rem;">
          Save your favourite products here and come back to them anytime.
        </p>
        <a href="/collections/all" class="button">Start Shopping</a>
      </div>
      <ul id="wishlist-grid" class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul>
    </div>`;

  const countEl = document.getElementById('wishlist-count-text');
  if (countEl) {
    const n = Wishlist.count();
    countEl.textContent = n === 0 ? '' : (n === 1 ? '1 item' : `${n} items`);
  }
}

function renderWishlistPage() {
  autoInjectWishlistContainer();

  const container = document.getElementById('wishlist-page-container');
  if (!container) return;

  const items = Wishlist.getItems();
  const emptyState = document.getElementById('wishlist-empty-state');
  const grid = document.getElementById('wishlist-grid');

  if (!items.length) {
    if (emptyState) emptyState.style.display = 'flex';
    if (grid) grid.innerHTML = '';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  if (grid) {
    /* Wrap each card in a <li class="grid__item"> — exactly like collection pages */
    grid.innerHTML = items
      .map((item) => `<li class="grid__item">${buildDawnCardHTML(item)}</li>`)
      .join('');

    /* When a wishlist button inside the page is toggled (remove), re-render */
    grid.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      /* State handled by delegation; we only need to watch for removal to re-render */
      btn.addEventListener('click', () => {
        /* Give delegation time to update localStorage first */
        requestAnimationFrame(() => {
          renderWishlistPage();
          updateWishlistCounter();
        });
      });
    });
  }
}

/* ─────────────────────────────────────────────
   Utilities
───────────────────────────────────────────── */
const _escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(str) {
  return (str || '').replace(/[&<>"']/g, (c) => _escapeMap[c]);
}

function escapeAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* ─────────────────────────────────────────────
   Backward-compat alias
───────────────────────────────────────────── */
function initWishlistButtons() { syncAllButtonStates(); }

/* ─────────────────────────────────────────────
   Bootstrap
───────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initWishlistDelegation();  // global delegated click handler
  syncAllButtonStates();     // fill hearts for already-wishlisted items
  updateWishlistCounter();   // header badge
  observeProductGrid();      // re-sync after AJAX filter / sort / pagination
  renderWishlistPage();      // render Dawn-native cards on /pages/wishlist
});

document.addEventListener('shopify:section:load', () => {
  syncAllButtonStates();
  updateWishlistCounter();
});

} // end of window.WishlistInitialized check
